<template>
    <div>
        <div class="modal fade" :class="{ 'show': showModal }" id="activityLogModal" tabindex="-1" role="dialog" :style="[showModal ? {'display': 'block'} : {'display': 'none'}]">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">История изменений - {{ modelTypeName }}</h5>
                    <button type="button" class="close" @click="closeModal">
                        <span>&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="loading" class="text-center">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Загрузка...
                    </div>

                    <div v-else>
                        <!-- Filters -->
                        <div class="row mb-3">
                            <div class="col-md-4">
                                <label>Событие:</label>
                                <select class="form-control" v-model="filters.event" @change="fetchLogs()">
                                    <option value="">Все</option>
                                    <option value="created">Создано</option>
                                    <option value="updated">Обновлено</option>
                                    <option value="deleted">Удалено</option>
                                </select>
                            </div>
                            <div class="col-md-4">
                                <label>Дата от:</label>
                                <input type="date" class="form-control" v-model="filters.date_from" @change="fetchLogs()">
                            </div>
                            <div class="col-md-4">
                                <label>Дата до:</label>
                                <input type="date" class="form-control" v-model="filters.date_to" @change="fetchLogs()">
                            </div>
                        </div>

                        <!-- Timeline -->
                        <div class="timeline timeline-3" v-if="logs.length > 0">
                            <div class="timeline-items" style="max-height: 500px; overflow-y: auto;">
                                <div class="timeline-item" v-for="log in logs" :key="log.id">
                                    <div class="timeline-media" :class="getTimelineBadgeClass(log.event)">
                                        <i :class="getEventIcon(log.event)"></i>
                                    </div>
                                    <div class="timeline-content">
                                        <div class="d-flex align-items-center justify-content-between mb-2">
                                            <div>
                                                <span class="badge" :class="getEventBadgeClass(log.event)">
                                                    {{ getEventText(log.event) }}
                                                </span>
                                                <span class="badge badge-light ml-2" v-if="log.properties && log.properties.relation">
                                                    {{ getRelationName(log.properties.relation) }}
                                                </span>
                                                <span class="text-muted ml-2">{{ formatDate(log.created_at) }}</span>
                                            </div>
                                            <button
                                                class="btn btn-sm btn-light-primary"
                                                @click="viewChanges(log.id)"
                                                v-if="log.event === 'updated' || log.event === 'created'">
                                                Все изменения
                                            </button>
                                        </div>
                                        <div class="text-dark-75 mb-2">
                                            <div><strong>{{ log.description || getEventText(log.event) }}</strong></div>
                                            <div>Пользователь: <strong>{{ log.causer ? log.causer.name : 'Система' }}</strong></div>
                                        </div>

                                        <!-- Changes Preview -->
                                        <div v-if="log.properties && Object.keys(log.properties).length > 0" class="changes-preview mt-2">
                                            <div class="changes-summary">
                                                <small class="text-muted">Изменено полей: {{ getChangesCount(log.properties) }}</small>
                                            </div>
                                            <div class="changes-list mt-2">
                                                <div v-for="(change, index) in getChangesPreview(log.properties)" :key="index" class="change-item mb-2 p-2 border-left border-primary">
                                                    <div class="change-field"><strong>{{ change.field }}</strong></div>
                                                    <div class="change-values">
                                                        <div class="old-value text-danger small">
                                                            <i class="fa fa-minus-circle"></i> Было:
                                                            <span>{{ isExpanded(log.id, index, 'old') ? change.oldFull : change.old }}</span>
                                                        </div>
                                                        <div class="mt-1" v-if="change.oldTruncated">
                                                        <button
                                                            v-if="change.oldTruncated"
                                                            @click="toggleExpand(log.id, index, 'old')"
                                                            class="btn btn-xs btn-link text-danger p-0 mr-2">
                                                            <i :class="isExpanded(log.id, index, 'old') ? 'fa fa-minus' : 'fa fa-plus'"></i>
                                                            {{ isExpanded(log.id, index, 'old') ? 'Скрыть' : 'Показать полностью' }}
                                                        </button>
                                                        </div>
                                                        <div class="new-value text-success small">
                                                            <i class="fa fa-plus-circle"></i> Стало:
                                                            <span>{{ isExpanded(log.id, index, 'new') ? change.newFull : change.new }}</span>
                                                        </div>
                                                        <div class="mt-1" v-if="change.newTruncated">
                                                            <button
                                                                v-if="change.newTruncated"
                                                                @click="toggleExpand(log.id, index, 'new')"
                                                                class="btn btn-xs btn-link text-success p-0">
                                                                <i :class="isExpanded(log.id, index, 'new') ? 'fa fa-minus' : 'fa fa-plus'"></i>
                                                                {{ isExpanded(log.id, index, 'new') ? 'Скрыть' : 'Показать полностью' }}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="alert alert-info">
                            История изменений пуста
                        </div>

                        <!-- Pagination -->
                        <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.last_page > 1">
                            <div>
                                Показано {{ pagination.from }} - {{ pagination.to }} из {{ pagination.total }}
                            </div>
                            <ul class="pagination mb-0">
                                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                    <a href="#" class="page-link" @click.prevent="changePage(pagination.current_page - 1)">Назад</a>
                                </li>
                                <li
                                    v-for="page in paginationPages"
                                    :key="page"
                                    class="page-item"
                                    :class="{ active: page === pagination.current_page }">
                                    <a href="#" class="page-link" @click.prevent="changePage(page)">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                                    <a href="#" class="page-link" @click.prevent="changePage(pagination.current_page + 1)">Вперед</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>

        <!-- Changes Detail Modal -->
        <div class="modal fade" :class="{ 'show': showChangesModal }" id="changesDetailModal" tabindex="-1" role="dialog" :style="[showChangesModal ? {'display': 'block'} : {'display': 'none'}]">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Детали изменений</h5>
                        <button type="button" class="close" @click="closeChangesModal">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div v-if="selectedActivity">
                            <div class="mb-3">
                                <strong>Событие:</strong>
                                <span class="badge" :class="getEventBadgeClass(selectedActivity.activity.event)">
                                    {{ getEventText(selectedActivity.activity.event) }}
                                </span>
                            </div>
                            <div class="mb-3">
                                <strong>Дата:</strong> {{ formatDate(selectedActivity.activity.created_at) }}
                            </div>
                            <div class="mb-3">
                                <strong>Пользователь:</strong> {{ selectedActivity.activity.causer ? selectedActivity.activity.causer.name : 'Система' }}
                            </div>

                            <h6 class="mt-4">Изменения:</h6>
                            <div v-if="selectedActivity.changes.length === 0" class="alert alert-info">
                                Нет записанных изменений
                            </div>
                            <table v-else class="table table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th style="width: 30%">Поле</th>
                                        <th style="width: 35%">Старое значение</th>
                                        <th style="width: 35%">Новое значение</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(change, index) in selectedActivity.changes" :key="index">
                                        <td><strong>{{ formatFieldName(change.field) }}</strong></td>
                                        <td>
                                            <span class="text-danger">{{ formatValue(change.old_value) }}</span>
                                        </td>
                                        <td>
                                            <span class="text-success">{{ formatValue(change.new_value) }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeChangesModal">Закрыть</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Backdrop -->
        <div v-if="showModal && !showChangesModal" class="modal-backdrop fade show" @click="closeModal"></div>
        <div v-if="showChangesModal" class="modal-backdrop fade show" @click="closeChangesModal"></div>
    </div>
</template>

<script>
export default {
    props: {
        modelType: {
            type: String,
            required: true
        },
        modelId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            logs: [],
            loading: false,
            showModal: false,
            showChangesModal: false,
            token: '',
            filters: {
                event: '',
                date_from: '',
                date_to: ''
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                total: 0,
                from: 0,
                to: 0
            },
            selectedActivity: null,
            expandedFields: {} // { logId: { fieldIndex: { old: boolean, new: boolean } } }
        };
    },
    computed: {
        modelTypeName() {
            const names = {
                'product': 'Товар',
                'order': 'Заказ',
                'customer': 'Клиент',
                'order_return': 'Возврат',
                'purchase': 'Закупка',
                'offer': 'Оффер'
            };
            return names[this.modelType] || this.modelType;
        },
        paginationPages() {
            let pages = [];
            let start = Math.max(1, this.pagination.current_page - 2);
            let end = Math.min(this.pagination.last_page, this.pagination.current_page + 2);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        }
    },
    methods: {
        async show() {
            this.showModal = true;
            await this.fetchLogs();
        },
        closeModal() {
            this.showModal = false;
        },
        closeChangesModal() {
            this.showChangesModal = false;
        },
        async fetchLogs(page = 1) {
            if (!this.modelId) return;

            this.loading = true;

            try {
                const config = {
                    ...this.token,
                    params: {
                        page: page,
                        ...this.filters
                    }
                };

                const response = await axios.get(`/api/admin/activity-logs/${this.modelType}/${this.modelId}`, config);

                if (response.data.status === 'Success') {
                    this.logs = response.data.data.activities.data;
                    this.pagination = {
                        current_page: response.data.data.activities.current_page,
                        last_page: response.data.data.activities.last_page,
                        total: response.data.data.activities.total,
                        from: response.data.data.activities.from,
                        to: response.data.data.activities.to
                    };
                }
            } catch (error) {
                console.error('Error fetching activity logs:', error);
                this.$toast.error('Ошибка загрузки истории');
            } finally {
                this.loading = false;
            }
        },
        async viewChanges(activityId) {
            try {
                const response = await axios.get(`/api/admin/activity-logs/compare/${activityId}`, this.token);

                if (response.data.status === 'Success') {
                    this.selectedActivity = response.data.data;
                    this.showChangesModal = true;
                }
            } catch (error) {
                console.error('Error fetching activity details:', error);
                this.$toast.error('Ошибка загрузки деталей');
            }
        },
        changePage(page) {
            if (page >= 1 && page <= this.pagination.last_page) {
                this.fetchLogs(page);
            }
        },
        getEventBadgeClass(event) {
            switch (event) {
                case 'created':
                    return 'badge-success';
                case 'updated':
                    return 'badge-primary';
                case 'deleted':
                    return 'badge-danger';
                default:
                    return 'badge-secondary';
            }
        },
        getTimelineBadgeClass(event) {
            switch (event) {
                case 'created':
                    return 'bg-success';
                case 'updated':
                    return 'bg-primary';
                case 'deleted':
                    return 'bg-danger';
                default:
                    return 'bg-secondary';
            }
        },
        getEventIcon(event) {
            switch (event) {
                case 'created':
                    return 'fa fa-plus';
                case 'updated':
                    return 'fa fa-edit';
                case 'deleted':
                    return 'fa fa-trash';
                default:
                    return 'fa fa-circle';
            }
        },
        getEventText(event) {
            switch (event) {
                case 'created':
                    return 'Создано';
                case 'updated':
                    return 'Обновлено';
                case 'deleted':
                    return 'Удалено';
                default:
                    return event;
            }
        },
        formatDate(dateString) {
            if (!dateString) return '-';

            const date = new Date(dateString);
            return date.toLocaleString('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        formatFieldName(field) {
            const fieldNames = {
                'title': 'Название',
                'price': 'Цена',
                'price_with_vat': 'Цена с НДС',
                'discount_price': 'Цена со скидкой',
                'description': 'Описание',
                'desc': 'Описание',
                'status': 'Статус',
                'product_status': 'Статус товара (LV)',
                'product_status_ee': 'Статус товара (EE)',
                'sku': 'Артикул',
                'ean': 'EAN',
                'barcode': 'Штрихкод',
                'order_price': 'Сумма заказа',
                'order_status_id': 'Статус заказа',
                'first_name': 'Имя',
                'last_name': 'Фамилия',
                'email': 'Email',
                'phone': 'Телефон',
                'product_details': 'Переводы товара',
                'categories': 'Категории',
                'combinations': 'Комбинации',
                'product_type': 'Тип товара',
                'brand_id': 'Бренд',
                'tax_rate': 'Ставка НДС',
                'is_featured': 'Рекомендуемый',
                'product_slug': 'URL слаг',
                'availability_status': 'Статус наличия',
                'b2b_enabled': 'Показывать в B2B',
                'b2b_price': 'Цена B2B',
                'discount_end_at': 'Скидка до',
                'purchase_price': 'Цена закупки',
                'quantity': 'Остаток'
            };

            // A variation's stock: "quantity: SKU".
            if (typeof field === 'string' && field.indexOf('quantity: ') === 0) {
                return 'Остаток (' + field.slice(10) + ')';
            }

            return fieldNames[field] || field;
        },
        truncateValue(value, maxLength = 100) {
            if (value === null || value === undefined) {
                return { truncated: '-', full: '-', isTruncated: false };
            }

            let strValue = '';
            if (typeof value === 'object') {
                strValue = JSON.stringify(value, null, 2);
            } else {
                strValue = String(value);
            }

            if (strValue.length > maxLength) {
                return {
                    truncated: strValue.substring(0, maxLength) + '...',
                    full: strValue,
                    isTruncated: true
                };
            }
            return {
                truncated: strValue,
                full: strValue,
                isTruncated: false
            };
        },
        getChangesCount(properties) {
            if (!properties) return 0;

            let count = 0;
            for (const key in properties) {
                if (key === 'product_details' || key === 'categories' || key === 'combinations') {
                    // Count as 1 for each complex field
                    count++;
                } else if (properties[key] && typeof properties[key] === 'object' && properties[key].old !== undefined) {
                    count++;
                }
            }
            return count;
        },
        getChangesPreview(properties) {
            if (!properties) return [];

            const changes = [];

            for (const key in properties) {
                const value = properties[key];

                // Handle nested old/new structure
                if (value && typeof value === 'object' && value.old !== undefined && value.new !== undefined) {
                    const oldValue = this.truncateValue(value.old);
                    const newValue = this.truncateValue(value.new);

                    changes.push({
                        field: this.formatFieldName(key),
                        old: oldValue.truncated,
                        oldFull: oldValue.full,
                        oldTruncated: oldValue.isTruncated,
                        new: newValue.truncated,
                        newFull: newValue.full,
                        newTruncated: newValue.isTruncated
                    });
                }
                // Handle direct values (created event)
                else if (key !== 'relation' && key !== 'detail_id' && key !== 'product_id' && key !== 'changes') {
                    const newValue = this.truncateValue(value);

                    changes.push({
                        field: this.formatFieldName(key),
                        old: '-',
                        oldFull: '-',
                        oldTruncated: false,
                        new: newValue.truncated,
                        newFull: newValue.full,
                        newTruncated: newValue.isTruncated
                    });
                }
            }

            return changes;
        },
        toggleExpand(logId, fieldIndex, type) {
            if (!this.expandedFields[logId]) {
                this.$set(this.expandedFields, logId, {});
            }
            if (!this.expandedFields[logId][fieldIndex]) {
                this.$set(this.expandedFields[logId], fieldIndex, { old: false, new: false });
            }
            this.$set(this.expandedFields[logId][fieldIndex], type, !this.expandedFields[logId][fieldIndex][type]);
        },
        isExpanded(logId, fieldIndex, type) {
            return this.expandedFields[logId]?.[fieldIndex]?.[type] || false;
        },
        formatValue(value) {
            if (value === null || value === undefined) {
                return '-';
            }
            if (typeof value === 'object') {
                return JSON.stringify(value);
            }
            return value;
        },
        getSubjectTypeName(subjectType) {
            const typeNames = {
                'App\\Models\\Admin\\Product': 'Товар',
                'App\\Models\\Admin\\ProductDetail': 'Перевод товара',
                'App\\Models\\Admin\\ProductCategory': 'Категория товара',
                'App\\Models\\Admin\\ProductCombination': 'Комбинация товара',
                'App\\Models\\Admin\\ProductCombinationDtl': 'Детали комбинации',
                'App\\Models\\Web\\Order': 'Заказ',
                'App\\Models\\Web\\OrderDetail': 'Позиция заказа',
                'App\\Models\\Admin\\Customer': 'Клиент',
                'App\\Models\\Admin\\OrderReturn': 'Возврат',
                'App\\Models\\Admin\\OrderReturnDetail': 'Позиция возврата',
                'App\\Models\\Admin\\Purchase': 'Закупка',
                'App\\Models\\Admin\\PurchaseDetail': 'Позиция закупки',
                'App\\Models\\Admin\\Offer': 'Оффер',
                'App\\Models\\Admin\\CategoryDetail': 'Перевод категории'
            };
            return typeNames[subjectType] || subjectType;
        },
        getRelationName(relation) {
            const relationNames = {
                'ProductDetail': 'Перевод товара',
                'ProductCategory': 'Категория',
                'ProductCombination': 'Комбинация',
                'ProductCombinationDtl': 'Вариация',
                'OrderDetail': 'Позиция заказа',
                'OrderReturnDetail': 'Позиция возврата',
                'PurchaseDetail': 'Позиция закупки'
            };
            return relationNames[relation] || relation;
        }
    },
    mounted() {
        var token = localStorage.getItem('token');
        this.token = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };
    }
};
</script>

<style scoped>
.timeline {
    position: relative;
    padding-left: 50px;
}

.timeline-item {
    position: relative;
    padding-bottom: 30px;
}

.timeline-item:last-child {
    padding-bottom: 0;
}

.timeline-media {
    position: absolute;
    left: -50px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.timeline-content {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 5px;
    border-left: 3px solid #dee2e6;
}

.timeline-item:before {
    content: '';
    position: absolute;
    left: -31px;
    top: 40px;
    bottom: -30px;
    width: 2px;
    background: #dee2e6;
}

.timeline-item:last-child:before {
    display: none;
}

.badge {
    padding: 5px 10px;
    font-size: 12px;
}

.modal-xl {
    max-width: 1200px;
}

/* Changes Preview Styles */
.changes-preview {
    background: #ffffff;
    border-radius: 4px;
    padding: 10px;
    border: 1px solid #e4e6ef;
}

.changes-summary {
    padding-bottom: 8px;
    border-bottom: 1px solid #e4e6ef;
    margin-bottom: 10px;
}

.change-item {
    background: #f9f9f9;
    border-radius: 4px;
    border-left: 3px solid #3699ff !important;
}

.change-field {
    color: #3f4254;
    font-size: 13px;
    margin-bottom: 5px;
}

.change-values {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.old-value, .new-value {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 4px 0;
    line-height: 1.4;
}

.old-value span, .new-value span {
    word-break: break-word;
    font-family: 'Courier New', monospace;
    background: #f4f4f4;
    padding: 2px 6px;
    border-radius: 3px;
    display: inline-block;
    max-width: 100%;
}

.old-value i {
    margin-top: 2px;
}

.new-value i {
    margin-top: 2px;
}

.btn-light-primary {
    background-color: #e1f0ff;
    color: #3699ff;
    border: none;
}

.btn-light-primary:hover {
    background-color: #3699ff;
    color: white;
}

.border-left {
    border-left-width: 3px !important;
}

.border-primary {
    border-color: #3699ff !important;
}

.btn-xs {
    font-size: 11px;
    padding: 2px 8px;
}

.btn-link {
    text-decoration: none;
}

.btn-link:hover {
    text-decoration: underline;
}

.old-value span, .new-value span {
    white-space: pre-wrap;
}
</style>
